import { useState, useEffect, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Camera, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RootState } from '@/redux/store';
import { IProjectMake } from './projectMake.d';
import {
  useDeleteProjectMakeMutation,
  useGetProjectMakesByGroupQuery,
  useGetProjectMakesByPostQuery,
} from './projectMake.api.slice';
import { StarDisplay } from '@/components/common/StarRating';
import ProfilePicture from '@/components/ui/profile-picture';
import { getDisplayName } from '@/lib/utils';
import { averageRating, weightedRating } from './makeRating';
import MakeDetail from './MakeDetail';
import SectionHeading from '@/components/Post/SectionHeading';

// ponytail: 5 tiles + a "+N more" tile fills two rows of three without a
// dedicated pagination control. Expanding just drops the cap.
const COLLAPSED_TILE_COUNT = 5;

interface ProjectMakesProps {
  /** Exactly one of postId / projectGroupId. */
  postId?: string;
  projectGroupId?: string;
  /** "Share your Make" call-to-action, rendered above the tile grid. */
  action?: ReactNode;
}

export default function ProjectMakes({
  postId,
  projectGroupId,
  action,
}: ProjectMakesProps) {
  const user = useSelector((state: RootState) => state.auth.user);
  const postQuery = useGetProjectMakesByPostQuery(
    { postId: postId ?? '' },
    { skip: !postId }
  );
  const groupQuery = useGetProjectMakesByGroupQuery(
    { projectGroupId: projectGroupId ?? '' },
    { skip: !projectGroupId }
  );
  const { data: makes, isLoading } = postId ? postQuery : groupQuery;
  const [deleteProjectMake, { isLoading: isDeleting }] =
    useDeleteProjectMakeMutation();
  const [makeToDelete, setMakeToDelete] = useState<string | null>(null);
  const [openMake, setOpenMake] = useState<IProjectMake | null>(null);
  const [showAll, setShowAll] = useState(false);

  // A #make-<id> deep link used to land on that make's full card. Tiles only
  // show a thumbnail, so open the linked make's detail instead — and expand the
  // grid first, or the target tile may not be rendered for the page to scroll to.
  useEffect(() => {
    const linkedId = window.location.hash.replace('#make-', '');
    if (!linkedId || !makes?.length) return;

    const linkedMake = makes.find((make) => make._id === linkedId);
    if (!linkedMake) return;

    setShowAll(true);
    setOpenMake(linkedMake);
  }, [makes]);

  const handleDelete = async (makeId: string) => {
    try {
      await deleteProjectMake({ makeId }).unwrap();
      toast.success('Make deleted successfully');
      setMakeToDelete(null);
    } catch (deleteError) {
      toast.error('Failed to delete make. Please try again.');
    }
  };

  const canDeleteMake = (make: IProjectMake) => user?._id === make.user?._id;

  const header = (
    <SectionHeading
      icon={<Wrench className="w-5 h-5 text-primary" />}
      count={makes?.length}
    >
      Makes
    </SectionHeading>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-square rounded-lg bg-muted animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!makes || makes.length === 0) {
    return (
      <div className="space-y-4">
        {header}
        <Card className="p-4 md:p-5 space-y-4">
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <Camera
              className="w-20 h-20 text-muted-foreground"
              strokeWidth={1}
            />
            <div>
              <h3 className="font-primary font-bold text-xl">No makes yet</h3>
              <p className="font-secondary text-sm text-muted-foreground">
                be the first to share how yours turned out
              </p>
            </div>
          </div>
          {action}
        </Card>
      </div>
    );
  }

  const average = averageRating(makes);
  const visibleMakes = showAll ? makes : makes.slice(0, COLLAPSED_TILE_COUNT);
  const hiddenCount = makes.length - visibleMakes.length;

  return (
    <div className="space-y-4">
      {header}

      <Card className="p-4 md:p-5 space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          {average && (
            <div className="flex items-center gap-2 shrink-0 leading-none">
              <StarDisplay rating={average} size="sm" />
              <span className="font-semibold">{average.toFixed(1)}</span>
            </div>
          )}
          <p className="text-sm text-muted-foreground flex-1 min-w-[16rem]">
            <span className="font-semibold text-foreground">
              {makes.length} {makes.length === 1 ? 'maker' : 'makers'}
            </span>{' '}
            {makes.length === 1 ? 'has' : 'have'} cut this and shared how it
            turned out — real proof it works on a real machine.
          </p>
        </div>

        {action}

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleMakes.map((make) => {
            const thumbnail = make.attachments?.[0]?.url;
            const rating = weightedRating(make.ratings);

            return (
              <button
                key={make._id}
                type="button"
                id={`make-${make._id}`}
                onClick={() => setOpenMake(make)}
                className="group relative aspect-square text-left scroll-mt-24 rounded-md border border-border overflow-hidden bg-card transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt={`Make by ${getDisplayName(make.user)}`}
                    // contain, not cover — a make photo cropped through its
                    // subject is worse than letterbox bars.
                    className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                  />
                ) : (
                  <span className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-muted-foreground" />
                  </span>
                )}

                {/* Meta rides on the photo so the tile stays mostly image. The
                    scrim keeps white text legible over an arbitrary photo. */}
                <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-gradient-to-t from-black/80 via-black/55 to-transparent px-3 pb-2.5 pt-8 text-white">
                  {rating ? (
                    <span className="flex items-center gap-1.5">
                      <StarDisplay rating={rating} size="sm" />
                      <span className="text-xs font-semibold">
                        {rating.toFixed(1)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-xs">
                      {make.isPrinted ? 'Cut' : 'Not cut'}
                    </span>
                  )}

                  <span className="flex items-center gap-2 min-w-0">
                    <ProfilePicture
                      src={make.user?.profilePicture}
                      alt={getDisplayName(make.user)}
                      className="w-6 h-6 shrink-0"
                      username={make.user?.username}
                    />
                    {/* Makes outlive their uploader — a deleted account leaves
                        the reference dangling, so don't render a bare "@". */}
                    <span
                      className={`text-xs truncate ${
                        make.user?.username ? '' : 'italic text-white/70'
                      }`}
                    >
                      {make.user?.username
                        ? `@${make.user.username}`
                        : 'Unknown maker'}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="aspect-square rounded-md border border-border bg-card flex items-center justify-center text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              +{hiddenCount} more {hiddenCount === 1 ? 'make' : 'makes'}
            </button>
          )}
        </div>
      </Card>

      {/* Make detail */}
      <Dialog
        open={!!openMake}
        onOpenChange={(open) => !open && setOpenMake(null)}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {/* Title is screen-reader only — the mockup leads with the photo,
              but Radix still needs a labelled dialog. */}
          <DialogHeader className="sr-only">
            <DialogTitle>Make details</DialogTitle>
            <DialogDescription>
              Photos, machine and ratings for this make.
            </DialogDescription>
          </DialogHeader>
          {openMake && (
            <MakeDetail
              make={openMake}
              onDelete={
                canDeleteMake(openMake)
                  ? () => {
                      // Close the detail first — stacked dialogs fight over focus.
                      setMakeToDelete(openMake._id);
                      setOpenMake(null);
                    }
                  : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!makeToDelete}
        onOpenChange={(open) => !open && setMakeToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Make</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this make? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMakeToDelete(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => makeToDelete && handleDelete(makeToDelete)}
              disabled={isDeleting}
              variant="destructive"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
